import React, { useState, useEffect } from 'react';
import { X, Calculator, Calendar, User, Clock, CheckCircle2, AlertTriangle, Layers, Loader2 } from 'lucide-react';
import styles from './Overtime.module.css';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import api from '../../../api/axios';
import toast from 'react-hot-toast';

const OTSalarySummaryModal = ({ isOpen, onClose }) => {
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    employee_id: '',
    month: String(new Date().getMonth() + 1),
    year: String(new Date().getFullYear())
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Month and Year Options
  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const years = Array.from({ length: 11 }, (_, i) => {
    const yr = String(2020 + i);
    return { value: yr, label: yr };
  });

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get('/employees-dropdown');
        if (response.data && response.data.data) {
          setEmployees(response.data.data.map(emp => ({
            value: emp.id,
            label: emp.employee_name
          })));
        } else if (response.data) {
          setEmployees(response.data.map(emp => ({
            value: emp.id,
            label: emp.employee_name
          })));
        }
      } catch (error) {
        console.error('Failed to load employee list:', error);
      }
    };

    if (isOpen) {
      fetchEmployees();
      setResult(null);
    }
  }, [isOpen]);

  const handleCalculate = async (e) => {
    e.preventDefault();
    if (!formData.employee_id) {
      toast.error('Please select an employee');
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const response = await api.get('/overtime/salary-summary', {
        params: {
          employee_id: formData.employee_id,
          month: formData.month,
          year: formData.year
        },
        skipLoader: true
      });

      if (response.data && response.data.success) {
        setResult(response.data.data);
        toast.success('OT Salary summary calculated successfully');
      } else {
        toast.error(response.data?.message || 'Failed to calculate overtime summary');
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Calculation failed. Please verify dates and records.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={onClose}>
      <div 
        className={styles['drawer-container']} 
        style={{ 
          maxWidth: '850px', 
          width: '90%', 
          height: '85vh', 
          borderRadius: '24px', 
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }} 
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles['drawer-header']} style={{ borderBottom: '1px solid #e2e8f0' }}>
          <div className={styles['header-title-wrapper']}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', color: 'white', padding: '8px', borderRadius: '12px', display: 'flex', alignItems: 'center' }}>
                <Calculator size={22} />
              </div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>OT Salary Summary</h2>
            </div>
            <p style={{ marginTop: '4px' }}>Query and calculate employee monthly overtime stats for payroll processing.</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Form Selection Row */}
        <form onSubmit={handleCalculate} style={{ padding: '24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '16px', alignItems: 'end' }}>
            <div className={styles['form-group']} style={{ margin: 0 }}>
              <label style={{ fontWeight: 700, fontSize: '13px', color: '#475569', marginBottom: '6px', display: 'block' }}>
                <User size={14} style={{ marginRight: '4px', inlineSize: '14px' }} /> Employee
              </label>
              <CustomSelect 
                options={employees}
                value={employees.find(e => e.value === formData.employee_id)}
                onChange={(opt) => setFormData({...formData, employee_id: opt ? opt.value : ''})}
                placeholder="Search Employee..."
                isClearable
              />
            </div>

            <div className={styles['form-group']} style={{ margin: 0 }}>
              <label style={{ fontWeight: 700, fontSize: '13px', color: '#475569', marginBottom: '6px', display: 'block' }}>
                <Calendar size={14} style={{ marginRight: '4px', inlineSize: '14px' }} /> Month
              </label>
              <CustomSelect 
                options={months}
                value={months.find(m => m.value === formData.month)}
                onChange={(opt) => setFormData({...formData, month: opt ? opt.value : ''})}
                placeholder="Select Month"
              />
            </div>

            <div className={styles['form-group']} style={{ margin: 0 }}>
              <label style={{ fontWeight: 700, fontSize: '13px', color: '#475569', marginBottom: '6px', display: 'block' }}>
                <Calendar size={14} style={{ marginRight: '4px', inlineSize: '14px' }} /> Year
              </label>
              <CustomSelect 
                options={years}
                value={years.find(y => y.value === formData.year)}
                onChange={(opt) => setFormData({...formData, year: opt ? opt.value : ''})}
                placeholder="Select Year"
              />
            </div>

            <button 
              type="submit" 
              className={styles['save-btn']} 
              style={{ height: '42px', padding: '0 20px', display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}
              disabled={loading}
            >
              {loading ? <Loader2 size={16} className={styles['spinner']} /> : <Calculator size={16} />}
              <span>Calculate</span>
            </button>
          </div>
        </form>

        {/* Scrollable Results Area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '12px' }}>
              <Loader2 size={40} className={styles['spinner']} style={{ color: '#f97316' }} />
              <p style={{ color: '#64748b', fontWeight: 600 }}>Calculating overtime statistics, please wait...</p>
            </div>
          )}

          {!loading && !result && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', textAlign: 'center', padding: '40px 0' }}>
              <Calculator size={48} strokeWidth={1.5} style={{ marginBottom: '16px', color: '#cbd5e1' }} />
              <h3 style={{ margin: 0, color: '#64748b', fontWeight: 700 }}>Select Employee Parameters</h3>
              <p style={{ margin: '6px 0 0 0', maxWidth: '380px', fontSize: '13px' }}>Select an employee and period above, then click Calculate to pull dynamic timesheets and overtime totals.</p>
            </div>
          )}

          {!loading && result && (
            <div>
              {/* Stat Boxes */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '16px', padding: '16px', display: 'flex', gap: '12px' }}>
                  <div style={{ background: '#ddd6fe', color: '#6d28d9', padding: '8px', borderRadius: '10px', height: '36px', width: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Clock size={18} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase' }}>Total Hours</label>
                    <h2 style={{ margin: '4px 0 0 0', fontSize: '22px', fontWeight: 800, color: '#5b21b6' }}>{result.total_ot_hours || '0.00'}h</h2>
                  </div>
                </div>

                <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '16px', padding: '16px', display: 'flex', gap: '12px' }}>
                  <div style={{ background: '#a7f3d0', color: '#047857', padding: '8px', borderRadius: '10px', height: '36px', width: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Layers size={18} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#059669', textTransform: 'uppercase' }}>Salary Days</label>
                    <h2 style={{ margin: '4px 0 0 0', fontSize: '22px', fontWeight: 800, color: '#065f46' }}>{result.total_salary_days || '0'} Days</h2>
                  </div>
                </div>

                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '16px', padding: '16px', display: 'flex', gap: '12px' }}>
                  <div style={{ background: '#bfdbfe', color: '#1d4ed8', padding: '8px', borderRadius: '10px', height: '36px', width: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircle2 size={18} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase' }}>Valid Shifts</label>
                    <h2 style={{ margin: '4px 0 0 0', fontSize: '22px', fontWeight: 800, color: '#1e40af' }}>{result.valid_ot_count || '0'}</h2>
                  </div>
                </div>

                <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '16px', padding: '16px', display: 'flex', gap: '12px' }}>
                  <div style={{ background: '#fed7d7', color: '#c53030', padding: '8px', borderRadius: '10px', height: '36px', width: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AlertTriangle size={18} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#e53e3e', textTransform: 'uppercase' }}>Invalid Shifts</label>
                    <h2 style={{ margin: '4px 0 0 0', fontSize: '22px', fontWeight: 800, color: '#9b2c2c' }}>{result.invalid_ot_count || '0'}</h2>
                  </div>
                </div>
              </div>

              {/* Assignment Breakdown */}
              <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 800, color: '#334155' }}>Completed Overtime Shifts</h3>
                  <span style={{ fontSize: '12px', background: '#e2e8f0', padding: '2px 8px', borderRadius: '6px', fontWeight: 700, color: '#475569' }}>
                    {result.assignments?.length || 0} Matches
                  </span>
                </div>
                
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: '12px 20px', fontSize: '10px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Date</th>
                      <th style={{ padding: '12px 20px', fontSize: '10px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Punch In</th>
                      <th style={{ padding: '12px 20px', fontSize: '10px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Punch Out</th>
                      <th style={{ padding: '12px 20px', fontSize: '10px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>OT Hours</th>
                      <th style={{ padding: '12px 20px', fontSize: '10px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Salary Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.assignments && result.assignments.length > 0 ? (
                      result.assignments.map((asgn, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 20px', fontSize: '13px', fontWeight: 600, color: '#334155' }}>{formatDate(asgn.ot_date)}</td>
                          <td style={{ padding: '12px 20px', fontSize: '13px', color: '#64748b' }}>{asgn.punch_in_at ? new Date(asgn.punch_in_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                          <td style={{ padding: '12px 20px', fontSize: '13px', color: '#64748b' }}>{asgn.punch_out_at ? new Date(asgn.punch_out_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                          <td style={{ padding: '12px 20px', fontSize: '13px', fontWeight: 700, color: '#4f46e5' }}>{asgn.ot_hours || '0.00'}h</td>
                          <td style={{ padding: '12px 20px', fontSize: '13px', fontWeight: 700, color: '#059669' }}>+{asgn.salary_days || '0'} Days</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                          No verified completed shifts found in the specified month.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OTSalarySummaryModal;
